import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';
import CoachingInsightCard from '../components/insights/CoachingInsightCard';

const KG_TO_LB = 2.20462;

const InsightsPage = () => {
  const { user } = useAuth();
  const [weeklyData, setWeeklyData] = useState(null);
  const [personalStats, setPersonalStats] = useState(null);
  const [latestWeight, setLatestWeight] = useState(null);
  const [moodText, setMoodText] = useState('');
  const [savingMood, setSavingMood] = useState(false);
  const [loading, setLoading] = useState(true);
  const weightUnit = user?.weight_unit || 'kg';

  const today = useMemo(() => new Date(), []);

  const formatDate = (d) => d.toISOString().split('T')[0];

  const getDateRange = useCallback((weeksAgo) => {
    const end = new Date(today);
    end.setDate(end.getDate() - weeksAgo * 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { start: formatDate(start), end: formatDate(end) };
  }, [today]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const thisWeek = getDateRange(0);
      const lastWeek = getDateRange(1);

      const [, , statsRes, fitbitRes] = await Promise.allSettled([
        apiClient.get('/api/health/entries/summary', { params: { target_date: thisWeek.end } }),
        apiClient.get('/api/health/entries/summary', { params: { target_date: lastWeek.end } }),
        apiClient.get('/api/personal-stats/'),
        apiClient.get('/api/fitbit/summary'),
      ]);

      // Also fetch all entries for this week and last week to get aggregates
      const [thisWeekEntries, lastWeekEntries] = await Promise.allSettled([
        apiClient.get('/api/health/entries', {
          params: { start_date: thisWeek.start, end_date: thisWeek.end, limit: 500 },
        }),
        apiClient.get('/api/health/entries', {
          params: { start_date: lastWeek.start, end_date: lastWeek.end, limit: 500 },
        }),
      ]);

      const aggregateCalories = (entries) => {
        if (!entries) return { consumed: 0, burned: 0, count: 0 };
        let consumed = 0, burned = 0;
        for (const e of entries) {
          const topCat = e.category_slug?.split('.')[0];
          if (e.calories) {
            if (topCat === 'nutrition') consumed += e.calories;
            else if (topCat === 'exercise') burned += e.calories;
          }
        }
        return { consumed: Math.round(consumed), burned: Math.round(burned), count: entries.length };
      };

      const thisEntries = thisWeekEntries.status === 'fulfilled' ? thisWeekEntries.value.data : [];
      const lastEntries = lastWeekEntries.status === 'fulfilled' ? lastWeekEntries.value.data : [];

      setWeeklyData({
        thisWeek: aggregateCalories(thisEntries),
        lastWeek: aggregateCalories(lastEntries),
      });

      if (statsRes.status === 'fulfilled') setPersonalStats(statsRes.value.data);
      if (fitbitRes.status === 'fulfilled') setLatestWeight(fitbitRes.value.data?.weight?.value);  // raw kg
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMoodSubmit = async () => {
    if (!moodText.trim()) return;
    setSavingMood(true);
    try {
      await apiClient.post('/api/health/entries', [{
        entry_date: formatDate(today),
        category_slug: 'mental_health.mood',
        entry_type: 'mood_check',
        source: 'manual',
        notes: moodText.trim(),
        rating: null,
        value: null,
        value_unit: null,
        data: { mood: moodText.trim() },
      }]);
      toast.success('Mood logged!');
      setMoodText('');
    } catch (error) {
      toast.error('Failed to save mood entry');
    } finally {
      setSavingMood(false);
    }
  };

  const formatDelta = (current, previous) => {
    if (!previous || previous === 0) return null;
    const diff = current - previous;
    const pct = Math.round((diff / previous) * 100);
    if (pct === 0) return { text: 'No change', color: 'text-gray-500' };
    return {
      text: `${pct > 0 ? '+' : ''}${pct}%`,
      color: pct > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
    };
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Insights</h1>
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const caloriesDelta = weeklyData
    ? formatDelta(weeklyData.thisWeek.consumed, weeklyData.lastWeek.consumed)
    : null;
  const burnedDelta = weeklyData
    ? formatDelta(weeklyData.thisWeek.burned, weeklyData.lastWeek.burned)
    : null;
  const entriesDelta = weeklyData
    ? formatDelta(weeklyData.thisWeek.count, weeklyData.lastWeek.count)
    : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Insights</h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Your health at a glance
        </p>
      </div>

      <div className="space-y-6">
        {/* AI Health Coach */}
        <CoachingInsightCard />

        {/* Weekly Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            {weeklyData ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-gray-50 dark:bg-surface-dark-raised rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Calories Consumed</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {weeklyData.thisWeek.consumed.toLocaleString()}
                  </p>
                  {caloriesDelta && (
                    <p className={`text-sm mt-1 ${caloriesDelta.color}`}>
                      {caloriesDelta.text} vs last week
                    </p>
                  )}
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-surface-dark-raised rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Calories Burned</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {weeklyData.thisWeek.burned.toLocaleString()}
                  </p>
                  {burnedDelta && (
                    <p className={`text-sm mt-1 ${burnedDelta.color}`}>
                      {burnedDelta.text} vs last week
                    </p>
                  )}
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-surface-dark-raised rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Entries</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {weeklyData.thisWeek.count}
                  </p>
                  {entriesDelta && (
                    <p className={`text-sm mt-1 ${entriesDelta.color}`}>
                      {entriesDelta.text} vs last week
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No data available yet. Start logging to see weekly comparisons.</p>
            )}
          </CardContent>
        </Card>

        {/* Goal Progress */}
        {personalStats && (
          <Card>
            <CardHeader>
              <CardTitle>Goal Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {personalStats.primary_goal && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Primary Goal</p>
                    <p className="text-gray-900 dark:text-white font-medium">{personalStats.primary_goal}</p>
                  </div>
                )}
                {personalStats.target_weight && (() => {
                  const wt = (v) => v != null ? +(weightUnit === 'lb' ? v * KG_TO_LB : v).toFixed(1) : null;
                  const displayCurrent = latestWeight ? wt(latestWeight) : (personalStats.weight ? wt(personalStats.weight) : null);
                  const displayTarget = wt(personalStats.target_weight);
                  return (
                  <div className="p-4 bg-gray-50 dark:bg-surface-dark-raised rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Current Weight</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {displayCurrent ? `${displayCurrent} ${weightUnit}` : '—'}
                        </p>
                      </div>
                      <div className="text-center px-4">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Target Weight</p>
                        <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
                          {displayTarget} {weightUnit}
                        </p>
                      </div>
                    </div>
                    {latestWeight && personalStats.target_weight && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <span>Progress</span>
                          <span>
                            {Math.abs(wt(latestWeight) - displayTarget).toFixed(1)} {weightUnit} to go
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all"
                            style={{
                              width: `${Math.min(100, Math.max(0,
                                personalStats.weight && personalStats.weight !== personalStats.target_weight
                                  ? ((personalStats.weight - latestWeight) / (personalStats.weight - personalStats.target_weight)) * 100
                                  : 0
                              ))}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
                {!personalStats.primary_goal && !personalStats.target_weight && (
                  <p className="text-gray-500 dark:text-gray-400">
                    Set your goals in your <a href="/me" className="text-primary-600 hover:underline">profile</a> to track progress here.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mood Check-in */}
        <Card>
          <CardHeader>
            <CardTitle>How are you feeling?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <input
                type="text"
                value={moodText}
                onChange={(e) => setMoodText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !savingMood && moodText.trim()) {
                    e.preventDefault();
                    handleMoodSubmit();
                  }
                }}
                placeholder="Happy, tired, anxious, motivated..."
                className="flex-1 min-w-0 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-raised px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <Button onClick={handleMoodSubmit} disabled={savingMood || !moodText.trim()} className="whitespace-nowrap shrink-0">
                {savingMood ? 'Saving...' : 'Log Mood'}
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default InsightsPage;
